import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { SEO } from "@/components/SEO";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, ArrowLeft, ArrowRight, ShieldCheck } from "lucide-react";

interface RelatedPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  category: string;
  read_time_minutes: number | null;
  published_at: string;
  featured_image: string | null;
}

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  featured_image: string | null;
  author_name: string;
  category: string;
  tags: string[];
  published_at: string;
  read_time_minutes: number | null;
  seo_title: string | null;
  seo_description: string | null;
}

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [related, setRelated] = useState<RelatedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug)
        .eq("status", "published")
        .maybeSingle();
      if (error || !data) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      const current = data as BlogPost;
      setPost(current);
      setLoading(false);

      // Fetch related: prefer same category + overlapping tags, fall back to recent.
      const { data: pool } = await supabase
        .from("blog_posts")
        .select("id,title,slug,excerpt,category,tags,read_time_minutes,published_at,featured_image")
        .eq("status", "published")
        .neq("id", current.id)
        .order("published_at", { ascending: false })
        .limit(40);

      const tagSet = new Set((current.tags ?? []).map((t) => t.toLowerCase()));
      const scored = (pool ?? [])
        .map((p: any) => {
          const overlap = (p.tags ?? []).reduce(
            (n: number, t: string) => n + (tagSet.has(t.toLowerCase()) ? 1 : 0),
            0,
          );
          const score = overlap * 3 + (p.category === current.category ? 2 : 0);
          return { p, score };
        })
        .sort((a, b) => b.score - a.score || +new Date(b.p.published_at) - +new Date(a.p.published_at))
        .slice(0, 4)
        .map(({ p }) => p as RelatedPost);

      setRelated(scored);
    })();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">Post not found.</p>
        <Button asChild variant="outline">
          <Link to="/blog">Back to blog</Link>
        </Button>
      </div>
    );
  }

  const canonical = `https://arqeraai.com/blog/${post.slug}`;
  const safeJsonLd = (obj: unknown) =>
    JSON.stringify(obj).replace(/</g, "\\u003c").replace(/>/g, "\\u003e").replace(/&/g, "\\u0026");
  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: post.featured_image ? [post.featured_image] : undefined,
    datePublished: post.published_at,
    dateModified: post.published_at,
    author: { "@type": "Organization", name: post.author_name || "ArqeraAI" },
    publisher: {
      "@type": "Organization",
      name: "ArqeraAI",
      url: "https://arqeraai.com",
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
    keywords: post.tags?.join(", "),
    articleSection: post.category,
  };
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://arqeraai.com/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://arqeraai.com/blog" },
      { "@type": "ListItem", position: 3, name: post.title, item: canonical },
    ],
  };

  return (
    <div className="min-h-screen bg-background py-12">
      <SEO
        title={post.seo_title ?? `${post.title}, ArqeraAI`}
        description={post.seo_description ?? post.excerpt}
        canonical={canonical}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(articleJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(breadcrumbJsonLd) }} />
      <article className="container mx-auto px-4 max-w-3xl">
        <Button asChild variant="ghost" size="sm" className="mb-6">
          <Link to="/blog" className="inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Back to blog
          </Link>
        </Button>

        <div className="flex items-center gap-2 mb-4">
          <Badge variant="secondary">{post.category}</Badge>
          {post.read_time_minutes && (
            <span className="text-sm text-muted-foreground">{post.read_time_minutes} min read</span>
          )}
        </div>

        <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4 leading-tight">
          {post.title}
        </h1>
        <p className="text-xl text-muted-foreground mb-6">{post.excerpt}</p>

        <div className="text-sm text-muted-foreground mb-8">
          {post.author_name} ·{" "}
          {new Date(post.published_at).toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </div>

        {post.featured_image && (
          <img
            src={post.featured_image}
            alt={post.title}
            className="w-full rounded-lg mb-10 aspect-video object-cover"
          />
        )}

        <div className="prose prose-invert max-w-none prose-headings:text-foreground prose-p:text-foreground/85 prose-a:text-primary prose-strong:text-foreground prose-li:text-foreground/85">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
        </div>

        {post.tags?.length > 0 && (
          <div className="flex gap-2 mt-12 flex-wrap">
            {post.tags.map((t) => (
              <Badge key={t} variant="outline" className="text-xs">{t}</Badge>
            ))}
          </div>
        )}

        <Card className="mt-16 p-8 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
          <div className="flex items-start gap-4">
            <div className="rounded-lg bg-primary/10 p-3 shrink-0">
              <ShieldCheck className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-semibold text-foreground mb-2">
                Ship AI features safely
              </h2>
              <p className="text-muted-foreground mb-6">
                ArqeraAI helps software and regulated teams find, fix, and validate AI feature
                risks before they expose customer data, break compliance, or stall enterprise
                deals.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button asChild size="lg">
                  <Link to="/ai-feature-security-review" className="inline-flex items-center gap-2">
                    Book an AI Feature Security Review <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <Link to="/methodology">Read methodology</Link>
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {related.length >= 3 && (
          <section className="mt-20" aria-labelledby="related-heading">
            <div className="flex items-end justify-between mb-6">
              <h2 id="related-heading" className="text-2xl font-semibold text-foreground">
                Related reading
              </h2>
              <Link to="/blog" className="text-sm text-primary hover:underline">
                All articles →
              </Link>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {related.map((r) => (
                <Link
                  key={r.id}
                  to={`/blog/${r.slug}`}
                  className="group block rounded-lg border border-border bg-card p-5 transition-colors hover:border-primary/40"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="secondary" className="text-xs">{r.category}</Badge>
                    {r.read_time_minutes && (
                      <span className="text-xs text-muted-foreground">
                        {r.read_time_minutes} min read
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors leading-snug">
                    {r.title}
                  </h3>
                  {r.excerpt && (
                    <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                      {r.excerpt}
                    </p>
                  )}
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="mt-12 flex items-center justify-between text-sm">
          <Button asChild variant="ghost" size="sm">
            <Link to="/blog" className="inline-flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" /> More articles
            </Link>
          </Button>
          <Link to="/security-intake" className="text-primary hover:underline">
            Talk to an engineer →
          </Link>
        </div>
      </article>
    </div>
  );
}
